import React, { Children } from 'react';
import { Building2, Users, Anchor, Share2 } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { PageBanner } from '../components/PageBanner';
export function HowWeWorkPage() {
  return <div data-id="element-503">
      <PageBanner title="Our Approach" subtitle="Practitioner-led funding built on 20 years of direct experience" image="https://cdn.magicpatterns.com/uploads/vyajbvAdVDqjRVYG1JVGaA/Teach_for_the_Philippines_-_Ambassadors_Program.png" data-id="element-504" />

      {/* Intro Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8" data-id="element-505">
        <div className="max-w-3xl mx-auto" data-id="element-506">
          <AnimatedSection delayClass="delay-200" data-id="element-507">
            <p className="text-xl md:text-2xl text-brand-brown leading-relaxed" data-id="element-508">
              Most funders come to this work from the outside. Firetree's team
              members have spent decades{' '}
              <span className="font-semibold text-brand-charcoal" data-id="element-509">
                founding, running and governing nonprofit organisations across
              </span>{' '}
              Southeast Asia simultaneously alongside the funding work.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-300" data-id="element-510">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-511">
              This is what we mean by practitioner-led funding. We assess
              organisations not against an arbitrary rubric in isolation, but
              against direct experience of doing the same work ourselves. We
              know what good looks like because we have had to build it, defend
              it, and sometimes rebuild it. Over more than 20 years and $100M
              deployed, that practitioner judgment has shaped every partnership
              we have formed and every decision we have made about where
              resources can do the most good.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Image Stack 1 — Single wide image */}
      <div className="w-full py-4" data-id="element-512">
        <AnimatedSection data-id="element-513">
          <div className="overflow-hidden" data-id="element-514">
            <img src="https://cdn.magicpatterns.com/uploads/13ZNrC12YdqbTTebs5W6Np/Teach_for_the_Philippines_-_KATIG_Teacher_Training.jpg" alt="Teachers in a training session in the Philippines" className="w-full h-72 md:h-[28rem] object-cover" data-id="element-515" />
          </div>
        </AnimatedSection>
      </div>

      {/* Section: We operate, not just fund */}
      <section className="py-10 px-4 sm:px-6 lg:px-8" data-id="element-516">
        <div className="max-w-3xl mx-auto" data-id="element-517">
          <AnimatedSection data-id="element-518">
            <div className="w-10 h-10 rounded-full bg-brand-card/60 flex items-center justify-center mb-4" data-id="element-519">
              <Building2 className="w-5 h-5 text-brand-red" data-id="element-520" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-5" data-id="element-521">
              We operate, not just fund
            </h2>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-100" data-id="element-522">
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-523">
              Tondo Community Initiative in Manila is our child protection
              initiative, serving one of the Philippines' most marginalised
              urban communities. Starfish Education Foundation in Thailand is
              our education innovation initiative, working directly with public
              schools, teachers and government agencies. We fully fund and run
              both.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-200" data-id="element-524">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-525">
              This matters because it keeps us grounded. We know what rigorous
              child protection looks like from the inside. We know what
              governance under pressure feels like. We know what it costs — in
              time, talent and resources — to deliver quality work at the
              community level. When we assess a partner organisation, we are not
              working from a framework. We are drawing on direct experiences of
              doing the same work ourselves.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Image Stack 2 — Single wide image */}
      <div className="w-full py-4" data-id="element-526">
        <AnimatedSection data-id="element-527">
          <div className="overflow-hidden" data-id="element-528">
            <img src="https://cdn.magicpatterns.com/uploads/1H6auDPwh9zzfUiYa5zatp/Teach_for_the_Philippines_-_Tara_Kaya_Program.png" alt="Children learning through the Tara Kaya literacy program" className="w-full h-72 md:h-[28rem] object-cover" data-id="element-529" />
          </div>
        </AnimatedSection>
      </div>

      {/* Section: We've been in the room */}
      <section className="py-10 px-4 sm:px-6 lg:px-8" data-id="element-530">
        <div className="max-w-3xl mx-auto" data-id="element-531">
          <AnimatedSection data-id="element-532">
            <div className="w-10 h-10 rounded-full bg-brand-card/60 flex items-center justify-center mb-4" data-id="element-533">
              <Users className="w-5 h-5 text-brand-red" data-id="element-534" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-5" data-id="element-535">
              We've been in the room
            </h2>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-100" data-id="element-536">
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-537">
              Firetree's principal has spent decades making the same kinds of
              decisions the leaders we fund are making now — about programme
              pivots, team management under pressure, and moments where the
              right path forward is genuinely unclear. When we sit with a
              partner, we are not offering an outside perspective. We know what
              it feels like to hold an organisation together through difficulty,
              to balance what a community needs against what the resources
              allow, and to manage the gap between what looks good on paper and
              what is actually happening on the ground.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-200" data-id="element-538">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-539">
              This is what it means to work as peers. Board experience across
              more than 20 organisations in Southeast Asia deepens that; it
              means we have seen how leadership teams behave when things are
              hard, not just when things are going well. But what positions us
              as peers is simpler than that: we have been and are in those
              shoes, and we bring that into every conversation we have with a
              partner.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Section: We stay */}
      <section className="py-10 px-4 sm:px-6 lg:px-8" data-id="element-540">
        <div className="max-w-3xl mx-auto" data-id="element-541">
          <AnimatedSection data-id="element-542">
            <div className="w-10 h-10 rounded-full bg-brand-card/60 flex items-center justify-center mb-4" data-id="element-543">
              <Anchor className="w-5 h-5 text-brand-red" data-id="element-544" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-5" data-id="element-545">
              We stay
            </h2>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-100" data-id="element-546">
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-547">
              Before we fund anyone, we spend significant time with the
              leadership, the team, and the communities they serve. We watch how
              decisions get made and how the organisation behaves when things
              are hard. We provide initial grant(s) specifically to create the
              time and space for that understanding to develop on both sides. By
              the time an organisation enters our Fund portfolio, we know it the
              way a colleague would.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-200" data-id="element-548">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-549">
              Our funding relationships span multiple years. Short funding
              cycles consume the energy that leaders should be spending on their
              work. When an organisation knows its funding relationship is
              stable, it can plan properly, hire the right people, adapt and
              evolve as they see the need to and do all of this in a timely
              manner, without the distraction of worrying about where the next
              resources are coming from. Leaders who trust that a funder will
              not exit at the first sign of difficulty are more likely to share
              the real challenges they face. That openness is what allows us to
              be genuinely useful not just as a source of funding, but as a
              thought partner when situations are complicated.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-300" data-id="element-550">
            <p className="text-xl text-brand-charcoal leading-relaxed mt-6 font-semibold font-serif" data-id="element-551">
              We have worked with some of our partners for over 15 years.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Section: We share who we back */}
      <section className="py-10 px-4 sm:px-6 lg:px-8" data-id="element-552">
        <div className="max-w-3xl mx-auto" data-id="element-553">
          <AnimatedSection data-id="element-554">
            <div className="w-10 h-10 rounded-full bg-brand-card/60 flex items-center justify-center mb-4" data-id="element-555">
              <Share2 className="w-5 h-5 text-brand-red" data-id="element-556" />
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-5" data-id="element-557">
              We share who we back
            </h2>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-100" data-id="element-558">
            <p className="text-lg text-brand-brown leading-relaxed" data-id="element-559">
              Every organisation in the Firetree Fund portfolio is one we
              currently fund ourselves — and intend to keep funding. These are
              not recent discoveries. They represent some of the most effective
              and operationally mature organisations in their field, and they
              have been through years of real partnership with us before being
              introduced to fellow funders.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-200" data-id="element-560">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-561">
              Organisations with demonstrated impact, strong leadership and
              established accountability mechanisms are considered for inclusion
              in the Firetree Fund portfolio. Before reaching this point, each
              has been through rigorous validation with us — assessed for
              financial health, leadership resilience under pressure, learning
              agility and a proven ability to deploy resources effectively.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-300" data-id="element-562">
            <p className="text-lg text-brand-brown leading-relaxed mt-6" data-id="element-563">
              We believe successful organisations should not be heavily
              dependent on a single or very few donors. The organisations we
              bring to the Fund maintain diversified funding bases — we would
              fund them more ourselves if that principle didn't hold us back.
              They are ready for more, and we know it.
            </p>
          </AnimatedSection>
          <AnimatedSection delayClass="delay-400" data-id="element-564">
            <div className="mt-6 p-8 bg-brand-card/30 rounded-2xl border border-brand-shape/50" data-id="element-565">
              <p className="text-lg text-brand-charcoal leading-relaxed font-medium italic" data-id="element-566">
                This is the offer the Firetree Fund makes: not a directory, but
                curated access to organisations tested through real partnership
                and assessed by people who are practitioners and funders
                themselves.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>;
}